import * as v from 'valibot';

/**
 * The context graph: one place that knows what everything here is, and what it
 * touches.
 *
 * The problem it solves is not retrieval. An agent asked "who owns the checkout
 * service and what did we change in it last week" can already reach twenty
 * systems one at a time — the repository, the issue tracker, the incident log,
 * the design docs — and it will, and each answer arrives as a page of JSON that
 * displaces the context it needed to ask the next question. Twenty round trips
 * and a full window later it has an answer that a graph would have returned in
 * one hop.
 *
 * So the graph is deliberately *thin*. A node is an identity, a title, a
 * sentence and a URL — never the document itself. The rule is that a node
 * carries enough to decide whether to go and read the real thing, and nothing
 * more: the moment it carries the thing, the graph is a second copy of every
 * system it federates and every answer it gives is as stale as its last sync.
 *
 * Nodes are addressed by `kind` plus a `key` that the *source* owns, so
 * re-ingesting is an upsert rather than a duplicate. `acme/web` ingested from
 * the push trigger and `acme/web` named by an incident are the same node, which
 * is the entire point: the edges only mean something if the endpoints unify.
 */

/**
 * What a node can be.
 *
 * A closed list, and short. An open `kind` field looks flexible and produces a
 * graph with `service`, `Service`, `svc` and `microservice` in it, at which
 * point no query can be written that returns all of them. Adding a kind is a
 * one-line diff here; inventing one at ingest time is not possible.
 */
export const GraphNodeKind = v.picklist([
  'service',
  'repo',
  'file',
  'symbol',
  'workflow',
  'agent',
  'skill',
  'work_item',
  'incident',
  'signal',
  'doc',
  'design_doc',
  'issue',
  'pull_request',
  'team',
  'person',
  'dataset',
  'endpoint',
  'flag',
  'environment',
]);
export type GraphNodeKind = v.InferOutput<typeof GraphNodeKind>;

/**
 * What an edge can mean.
 *
 * Every edge is directed and reads as a sentence: `from` *verb* `to`.
 * `checkout owns pricing-lib` and `pricing-lib owns checkout` are different
 * claims, and a graph that stored them as one undirected "related" edge could
 * answer neither "what does this depend on" nor "what breaks if I change it".
 */
export const GraphEdgeKind = v.picklist([
  'owns',
  'depends_on',
  'calls',
  'defines',
  'contains',
  'documents',
  'implements',
  'caused_by',
  'fixes',
  'released_by',
  'member_of',
  'references',
  'tested_by',
  'triggers',
  'runs',
  'mitigates',
  'duplicate_of',
]);
export type GraphEdgeKind = v.InferOutput<typeof GraphEdgeKind>;

/**
 * Where a node came from, and therefore who is allowed to overwrite it.
 *
 * An ingestion only ever replaces the nodes and edges *it* wrote. Without that
 * rule the push trigger's re-index would delete the incident graph, and the
 * first person to debug it would conclude the graph is unreliable and stop
 * using it — which is the failure mode a shared graph actually dies of.
 */
export const GraphSource = v.picklist([
  'code_index',
  'workflow',
  'factory',
  'monitoring',
  'github',
  'linear',
  'sentry',
  'docs',
  'scim',
  'manual',
]);
export type GraphSource = v.InferOutput<typeof GraphSource>;

export const GraphNode = v.object({
  kind: GraphNodeKind,
  /** Stable within `(kind, source)` and owned by the source: `acme/web`, `INC-412`. */
  key: v.pipe(v.string(), v.minLength(1), v.maxLength(400)),
  title: v.pipe(v.string(), v.minLength(1), v.maxLength(400)),
  /** One sentence. Not a description — the thing itself is one fetch away. */
  summary: v.optional(v.nullable(v.pipe(v.string(), v.maxLength(600))), null),
  url: v.optional(v.nullable(v.string()), null),
  source: GraphSource,
  /** The team that answers for it, when the source knows. */
  owner: v.optional(v.nullable(v.string()), null),
  /**
   * Whatever else the source knew, small. Capped by the ingest path rather than
   * here, because the cap belongs where the bytes are counted.
   */
  attributes: v.optional(v.record(v.string(), v.unknown()), {}),
  updatedAt: v.optional(v.string()),
});
export type GraphNode = v.InferOutput<typeof GraphNode>;

export const GraphEdge = v.object({
  kind: GraphEdgeKind,
  fromKind: GraphNodeKind,
  fromKey: v.string(),
  toKind: GraphNodeKind,
  toKey: v.string(),
  source: GraphSource,
  /**
   * How strong the claim is, 0–1. Used only for ordering: a hundred `calls`
   * edges out of one file are not a hundred equally interesting answers, and
   * the alternative to ranking them is truncating them arbitrarily.
   */
  weight: v.optional(v.pipe(v.number(), v.minValue(0), v.maxValue(1)), 1),
  attributes: v.optional(v.record(v.string(), v.unknown()), {}),
});
export type GraphEdge = v.InferOutput<typeof GraphEdge>;

/** One ingestion: the nodes and edges a source is asserting, wholesale. */
export const GraphIngest = v.object({
  source: GraphSource,
  /**
   * Narrows what this ingestion replaces. A push for `acme/web` should not
   * delete the code-index nodes of every other repository, so it declares the
   * slice it owns and only that slice is swept.
   */
  scope: v.optional(v.nullable(v.pipe(v.string(), v.maxLength(200))), null),
  nodes: v.array(GraphNode),
  edges: v.optional(v.array(GraphEdge), []),
  /**
   * Delete anything in `(source, scope)` this ingestion did not mention.
   *
   * On by default for the same reason the code index is rebuilt rather than
   * appended: a graph that only ever grows answers questions about services
   * that were deleted two quarters ago, confidently.
   */
  sweep: v.optional(v.boolean(), true),
});
export type GraphIngest = v.InferOutput<typeof GraphIngest>;

/** A node as a query returns it, with its identity resolved to one string. */
export const GraphNodeRecord = v.object({
  ...GraphNode.entries,
  /** `kind:key`. What an edge points at and what a caller passes back in. */
  id: v.string(),
  updatedAt: v.string(),
});
export type GraphNodeRecord = v.InferOutput<typeof GraphNodeRecord>;

/**
 * Answer caps.
 *
 * The graph exists to make an answer fit in a prompt. An uncapped neighbourhood
 * query on a popular node returns the whole tree and costs more context than
 * the twenty round trips it replaced, so every query says how much it withheld
 * rather than quietly returning everything it found.
 */
export const GRAPH_LIMITS = {
  /** Nodes per search answer. */
  search: 25,
  /** Edges per direction on a neighbourhood answer. */
  neighbours: 30,
  /** Hops a traversal will take. Three is where "related" stops meaning anything. */
  depth: 3,
  /** Nodes an impact answer names. */
  impact: 40,
  /** Nodes one ingestion may assert. Past this it is a sync job, not an ingestion. */
  ingestNodes: 5_000,
  /** Bytes of `attributes` per node — the line between an index and a copy. */
  attributeBytes: 2_000,
} as const;

export function graphNodeId(kind: GraphNodeKind, key: string): string {
  return `${kind}:${key}`;
}

/**
 * Split a `kind:key` back into its halves.
 *
 * Only the first colon separates them: a key is very often a URL or a path and
 * splitting on every colon turns `doc:https://wiki/x` into a node kind of `doc`
 * and a key of `https`, which resolves to nothing and reads like a missing node
 * rather than a parsing bug.
 */
export function parseGraphNodeId(
  id: string,
): { kind: GraphNodeKind; key: string } | null {
  const separator = id.indexOf(':');
  if (separator <= 0) return null;

  const kind = id.slice(0, separator);
  const key = id.slice(separator + 1);
  if (!key) return null;

  const parsed = v.safeParse(GraphNodeKind, kind);
  return parsed.success ? { kind: parsed.output, key } : null;
}
